/**
 * Derives submission due time from checklist schedule fields + execution start.
 *
 * **ปฏิทิน “วันที่”** ใช้เขตเดียวกับลาว/ไทย (UTC+7, ไม่มี DST) เพื่อให้ “ກຳນົດສົ່ງວັນທີ 4”
 * ตรงกับที่แสดงใน `formatDate` บน client ที่มักเป็น +7 — ไม่ใช่สิ้นวัน UTC ที่เลื่อนเป็นวันถัดไปเมื่อแปลงเป็น local
 */

export type ChecklistScheduleInput = {
  scheduleType: "daily" | "opening" | "closing" | "scheduled";
  scheduleFrequency: "daily" | "weekly" | "monthly" | null;
  scheduleDayOfWeek: number | null;
  scheduleDayOfMonth: number | null;
};

/**
 * เขตเวลาอ้างอิงสำหรับกำหนด “สิ้นวัน” ของ checklist (ชั่วโมงนำหน้า UTC)
 * TODO: อาจย้ายไป env เช่น CHECKLIST_SCHEDULE_OFFSET_HOURS หรือ timeZone IANA ถ้ามีหลายประเทศ
 */
export const CHECKLIST_SCHEDULE_OFFSET_HOURS = 7;

function daysInMonthUtc(year: number, monthIndex0: number): number {
  return new Date(Date.UTC(year, monthIndex0 + 1, 0)).getUTCDate();
}

/** Parse YYYY-MM-DD from DB timestamp string (ถือเป็นวันที่ปฏิทินในธุรกิจ +7) */
function civilDatePartsFromDbTimestamp(s: string): {
  y: number;
  m0: number;
  d: number;
} | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d))
    return null;
  return { y, m0: mo, d };
}

const offMs = () => CHECKLIST_SCHEDULE_OFFSET_HOURS * 60 * 60 * 1000;

/**
 * สิ้นวันตามปฏิทิน (y, m0, d) ใน UTC+offset: 23:59:59.999 ตามนาฬิกาท้องถิ่น → ISO (UTC)
 */
function endOfOffsetWallDayIso(y: number, m0: number, d: number): string {
  const off = offMs();
  const nextCivilDayStartUtc =
    Date.UTC(y, m0, d + 1, 0, 0, 0, 0) - off;
  return new Date(nextCivilDayStartUtc - 1).toISOString();
}

/** เที่ยงวันของวันที่ civil (y,m0,d) ใน offset — ใช้หา weekday */
function civilNoonUtcMs(y: number, m0: number, d: number): number {
  return Date.UTC(y, m0, d, 12, 0, 0, 0) - offMs();
}

/** Schema: 1=Mon … 7=Sun → JS getUTCDay (0=Sun … 6=Sat) */
function schemaDowToUtcJsDow(schemaDow: number): number {
  if (schemaDow === 7) return 0;
  if (schemaDow >= 1 && schemaDow <= 6) return schemaDow;
  return 1;
}

/**
 * Weekday ของวันที่ civil ใน offset ที่เที่ยงวัน (หลีกเลี่ยงขอบเขตวัน)
 */
function civilWeekdayJs(y: number, m0: number, d: number): number {
  return new Date(civilNoonUtcMs(y, m0, d)).getUTCDay();
}

function dueEndOfSameScheduleDay(startedAt: string): string | null {
  const parts = civilDatePartsFromDbTimestamp(startedAt);
  if (!parts) return null;
  return endOfOffsetWallDayIso(parts.y, parts.m0, parts.d);
}

function dueWeeklyEnd(schemaDow: number, startedAt: string): string | null {
  const parts = civilDatePartsFromDbTimestamp(startedAt);
  if (!parts) return null;
  const target = schemaDowToUtcJsDow(schemaDow);
  for (let i = 0; i < 14; i++) {
    const y = parts.y;
    const m0 = parts.m0;
    const d = parts.d + i;
    if (civilWeekdayJs(y, m0, d) === target) {
      return endOfOffsetWallDayIso(y, m0, d);
    }
  }
  return endOfOffsetWallDayIso(parts.y, parts.m0, parts.d);
}

function dueMonthlyEnd(dayOfMonth: number, startedAt: string): string | null {
  const parts = civilDatePartsFromDbTimestamp(startedAt);
  if (!parts) return null;
  const dom = Math.min(Math.max(1, dayOfMonth), 31);

  const buildEnd = (y: number, m0: number): string | null => {
    const dim = daysInMonthUtc(y, m0);
    const d = Math.min(dom, dim);
    return endOfOffsetWallDayIso(y, m0, d);
  };

  let y = parts.y;
  let m0 = parts.m0;
  const startNoon = civilNoonUtcMs(parts.y, parts.m0, parts.d);
  const candidate = buildEnd(y, m0);
  if (!candidate) return null;
  if (new Date(candidate).getTime() >= startNoon) return candidate;

  m0 += 1;
  if (m0 > 11) {
    m0 = 0;
    y += 1;
  }
  return buildEnd(y, m0);
}

/**
 * When the employee has an in-progress round, when they should submit by (ISO string).
 * Returns null if no applicable due (e.g. not started, already completed, or unknown schedule).
 */
export function computeSubmissionDueAtIso(
  schedule: ChecklistScheduleInput,
  executionStatus: string,
  executionStartedAt: string | null,
): string | null {
  if (executionStatus !== "in_progress" || !executionStartedAt?.trim()) {
    return null;
  }

  const {
    scheduleType,
    scheduleFrequency,
    scheduleDayOfWeek,
    scheduleDayOfMonth,
  } = schedule;

  if (
    scheduleType === "daily" ||
    scheduleType === "opening" ||
    scheduleType === "closing"
  ) {
    return dueEndOfSameScheduleDay(executionStartedAt);
  }

  if (scheduleType !== "scheduled") {
    return dueEndOfSameScheduleDay(executionStartedAt);
  }

  const freq = scheduleFrequency ?? "daily";
  if (freq === "daily") {
    return dueEndOfSameScheduleDay(executionStartedAt);
  }

  if (freq === "weekly") {
    if (
      scheduleDayOfWeek != null &&
      scheduleDayOfWeek >= 1 &&
      scheduleDayOfWeek <= 7
    ) {
      return dueWeeklyEnd(scheduleDayOfWeek, executionStartedAt);
    }
    return dueEndOfSameScheduleDay(executionStartedAt);
  }

  if (freq === "monthly") {
    if (
      scheduleDayOfMonth != null &&
      scheduleDayOfMonth >= 1 &&
      scheduleDayOfMonth <= 31
    ) {
      return dueMonthlyEnd(scheduleDayOfMonth, executionStartedAt);
    }
    return dueEndOfSameScheduleDay(executionStartedAt);
  }

  return dueEndOfSameScheduleDay(executionStartedAt);
}

/** ชั่วโมงก่อนกำหนดส่งที่ถือว่า “ใกล้ครบ” (Phase 1 in-app alert) */
export const CHECKLIST_DUE_SOON_HOURS = 24;

export type ChecklistAlertStatus = "none" | "due_soon" | "overdue";

/**
 * สถานะแจ้งเตือนแบบคำนวณสด สำหรับรอบ in_progress ที่มี submissionDueAt
 * - overdue: เลยกำหนดส่งแล้ว
 * - due_soon: ยังไม่เลย แต่เหลือเวลา ≤ CHECKLIST_DUE_SOON_HOURS
 */
export function computeChecklistAlertStatus(
  submissionDueAt: string | null,
  executionStatus: string,
  nowMs: number = Date.now(),
): ChecklistAlertStatus {
  if (executionStatus !== "in_progress" || !submissionDueAt?.trim()) {
    return "none";
  }
  const due = Date.parse(submissionDueAt);
  if (!Number.isFinite(due)) return "none";
  if (nowMs > due) return "overdue";
  const msUntilDue = due - nowMs;
  const dueSoonMs = CHECKLIST_DUE_SOON_HOURS * 60 * 60 * 1000;
  if (msUntilDue <= dueSoonMs) return "due_soon";
  return "none";
}
